import {
    useCallback,
    useMemo,
    useState,
} from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import LoadingShape from 'components/LoadingShape';
import Drawer from 'components/Drawer';
import { Todo } from 'data/firestore/agent/todo/types';
import { EntityGet } from 'data/firestore/types';
import { ViewContextType } from 'pages/Opportunities/types';
import Filters from '../filters';
import useOpportunitiesTodosViewData from './hooks/useOpportunitiesTodosViewData';
import OpportunityTodosCard from './components/OpportunityTodosCard';
import {
    CardsContainer,
    CardsScrollContainer,
    EmptyState,
    FiltersSidebar,
    OverdueAlertBanner,
    TodosViewContainer,
} from './styled';

const OpportunitiesViewTodos = () => {
    const { agentId = '' } = useParams();
    const {
        selectedPipeline = '',
    } = useOutletContext<ViewContextType>();

    const {
        cardsData,
        isLoading,
        matchedTodosCount,
        overdueTodos,
        overdueTodosCount,
    } = useOpportunitiesTodosViewData({ agentId });
    const [isOverdueDrawerOpen, setIsOverdueDrawerOpen] = useState(false);

    const isEmpty = useMemo(
        () => !isLoading && cardsData.length === 0,
        [isLoading, cardsData.length],
    );
    const overdueCardsData = useMemo(
        () => overdueTodos.reduce(
            (acc, todo) => {
                const card = acc[todo.opportunityId];
                if (card) {
                    card.todos.push(todo);
                    return acc;
                }
                acc[todo.opportunityId] = {
                    opportunityId: todo.opportunityId,
                    todos: [todo],
                };
                return acc;
            },
            {} as Record<string, { opportunityId: string; todos: EntityGet<Todo>[] }>,
        ),
        [overdueTodos],
    );
    const overdueCards = useMemo(
        () => Object.values(overdueCardsData),
        [overdueCardsData],
    );
    const handleShowOverdueTodos = useCallback(
        () => {
            setIsOverdueDrawerOpen(true);
        },
        [],
    );
    const handleCloseOverdueDrawer = useCallback(
        () => setIsOverdueDrawerOpen(false),
        [],
    );

    return (
        <TodosViewContainer>
            <FiltersSidebar>
                <Filters
                    agentId={agentId}
                    pipelineId={selectedPipeline}
                    viewType="todos"
                    showCloseButton={false}
                />
            </FiltersSidebar>
            <CardsContainer>
                {overdueTodosCount > 0 && (
                    <OverdueAlertBanner onClick={handleShowOverdueTodos} type="button">
                        {`${overdueTodosCount} overdue tasks need attention. Click to view overdue tasks.`}
                    </OverdueAlertBanner>
                )}
                <CardsScrollContainer>
                    {isLoading && <LoadingShape />}
                    {isEmpty && (
                        <EmptyState>
                            {matchedTodosCount === 0
                                ? 'No TODOs match the current filters.'
                                : 'No opportunities available for the matched TODOs.'}
                        </EmptyState>
                    )}
                    {cardsData.map((card) => (
                        <OpportunityTodosCard
                            key={card.opportunityId}
                            agentId={agentId}
                            selectedPipeline={selectedPipeline}
                            opportunityId={card.opportunityId}
                            todos={card.todos}
                        />
                    ))}
                </CardsScrollContainer>
            </CardsContainer>
            <Drawer
                isOpen={isOverdueDrawerOpen}
                onRequestClose={handleCloseOverdueDrawer}
                header="Overdue TODOs"
                slideFrom="right"
                size="medium"
            >
                <CardsScrollContainer className="rainbow-p-around_medium">
                    {overdueCards.map((card) => (
                        <OpportunityTodosCard
                            key={card.opportunityId}
                            agentId={agentId}
                            selectedPipeline={selectedPipeline}
                            opportunityId={card.opportunityId}
                            todos={card.todos}
                        />
                    ))}
                </CardsScrollContainer>
            </Drawer>
        </TodosViewContainer>
    );
};

export default OpportunitiesViewTodos;
